import { useEffect, useState } from "react";
import { imgUrl } from "src/Helpers/utils/constants";

const checkImageExists = (fullUrl: string): Promise<boolean> => {
	return new Promise((resolve) => {
		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => resolve(true);
		img.onerror = () => resolve(false);
		img.src = fullUrl;
	});
};

type ValidImage<T> = T extends string ? string : string[];

/**
 * * Custom hook to validate if image URLs are accessible.
 * @description If an image fails to load, it will be replaced with a fallback placeholder.
 * Automatically prepends the image host base (`imgUrl`) to each input path.
 *
 * @param input - A single image path or an array of image paths (relative to the `imgUrl` base).
 * @param placeholder - Fallback image URL to use when the given image(s) fail to load.
 *
 * @returns A single valid image URL string or an array of valid image URL strings (with fallback applied as needed).
 *
 * @example
 * const avatar = useValidImage("user/avatar.jpg"); // `as string`
 * const avatars = useValidImage(["img1.jpg", "img2.jpg"]); // `as string[]`
 */
export function useValidImage<T extends string | string[]>(
	input?: T,
	placeholder: string = "/static/images/avatars/avatar2.png"
): ValidImage<T> {
	const [validImages, setValidImages] = useState<string | string[]>(
		Array.isArray(input) ? input.map(() => placeholder) : placeholder
	);

	useEffect(() => {
		let isMounted = true;

		const normalize = (url: string) => `${imgUrl}/${url}`;

		const validate = async () => {
			if (Array.isArray(input)) {
				const results = await Promise.all(
					input.map(async (url) => {
						const fullUrl = normalize(url);

						const isValid = await checkImageExists(fullUrl);

						return isValid ? fullUrl : placeholder;
					})
				);

				if (isMounted) {
					setValidImages(results);
				}
			} else {
				const fullUrl = normalize(input);
				const isValid = await checkImageExists(fullUrl);

				if (isMounted) {
					setValidImages(isValid ? fullUrl : placeholder);
				}
			}
		};

		if (input) {
			validate();
		}

		return () => {
			isMounted = false;
		};
	}, [input, placeholder]);

	return validImages as ValidImage<T>;
}
